/**
 * Chat Feature - Main Chat Page
 *
 * Displays chat list and messages with real-time WebSocket updates
 */

import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { api } from '../../../lib/api';
import type { APIResponse, SessionResponse } from '@room/shared-ui';
import { Container, Shell, isBundleMode } from '@room/shared-ui';
import { ChatListPanel, ChatThreadPanel } from '../components';
import { useChatSocket } from '../hooks';
import { fetchChats, fetchMessages, sendMessage as sendMessageApi } from '../api/chatApi';
import type { ChatMessage, ChatSummary } from '../types';

export default function ChatPage() {
  const [chats, setChats] = useState<ChatSummary[]>([]);
  const [selectedChatId, setSelectedChatId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [messageDraft, setMessageDraft] = useState('');
  const [isLoadingChats, setIsLoadingChats] = useState(true);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [typingUsers, setTypingUsers] = useState<string[]>([]);
  const [showMobileMessages, setShowMobileMessages] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const typingTimeoutRef = useRef<number | null>(null);
  const isBundle = isBundleMode();

  // WebSocket connection via custom hook
  const { connectionStatus, sendTyping } = useChatSocket({
    chatId: selectedChatId,
    userId: currentUserId,
    onMessage: (newMessage) => {
      setMessages((prev) => {
        if (prev.some((msg) => msg.id === newMessage.id)) {
          return prev;
        }
        return [...prev, newMessage];
      });
    },
    onTyping: (userId, isTyping) => {
      setTypingUsers((prev) => {
        const next = new Set(prev);
        if (isTyping) {
          next.add(userId);
        } else {
          next.delete(userId);
        }
        return Array.from(next);
      });
    },
  });

  // Load current user session
  useEffect(() => {
    const loadSession = async () => {
      try {
        const response = await api.get<APIResponse<SessionResponse>>('/auth/session');
        if (response.success && response.data?.user) {
          setCurrentUserId(response.data.user.id);
        }
      } catch {
        // session handled elsewhere
      }
    };

    void loadSession();
  }, []);

  // Load chats on mount
  useEffect(() => {
    const loadChats = async () => {
      setIsLoadingChats(true);
      setError(null);
      try {
        const loaded = await fetchChats();
        setChats(loaded);
        if (loaded.length > 0 && !selectedChatId) {
          setSelectedChatId(loaded[0].id);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load chats');
      } finally {
        setIsLoadingChats(false);
      }
    };

    void loadChats();
  }, []);

  // Load messages when chat is selected
  useEffect(() => {
    const loadMessages = async () => {
      if (!selectedChatId) return;
      setIsLoadingMessages(true);
      setError(null);
      try {
        const loaded = await fetchMessages(selectedChatId);
        setMessages(loaded);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load messages');
      } finally {
        setIsLoadingMessages(false);
      }
    };

    void loadMessages();
  }, [selectedChatId]);

  useEffect(() => {
    setMessageDraft('');
  }, [selectedChatId]);

  const activeChat = useMemo(
    () => chats.find((chat) => chat.id === selectedChatId) ?? null,
    [chats, selectedChatId]
  );
  const memberLabel = useMemo(() => {
    if (!activeChat) return null;
    const memberCount = activeChat.participants?.length ?? 0;
    if (memberCount === 0) return null;
    return `${memberCount} ${memberCount === 1 ? 'member' : 'members'}`;
  }, [activeChat]);

  const getChatDisplayName = (chat: ChatSummary): string => {
    // If name exists and doesn't look like an ID, use it
    if (chat.name && !chat.name.startsWith('DM-') && chat.name.length > 0) {
      return chat.name;
    }

    // For direct messages, try to get participant username
    if (chat.type === 'direct' && chat.participants && chat.participants.length > 0) {
      const otherParticipant = chat.participants.find((p) => p.id !== currentUserId);
      if (otherParticipant?.username) {
        return otherParticipant.username;
      }
    }

    // For group chats with participants
    if (chat.type === 'group' && chat.participants && chat.participants.length > 0) {
      const names = chat.participants
        .slice(0, 3)
        .map((p) => p.username)
        .filter(Boolean);
      if (names.length > 0) {
        return names.join(', ');
      }
    }

    // Fallback to chat name or generic label
    return chat.name || (chat.type === 'direct' ? 'Direct Message' : 'Group Chat');
  };

  const formatChatTimestamp = (chat: ChatSummary): string | null => {
    const timestamp = chat.lastMessage?.timestamp ?? chat.updatedAt;
    if (!timestamp) return null;

    const date = new Date(timestamp);
    const now = new Date();
    if (date.toDateString() === now.toDateString()) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const filteredChats = useMemo(() => {
    if (!searchQuery.trim()) return chats;

    const query = searchQuery.toLowerCase();
    return chats.filter((chat) => {
      const displayName = getChatDisplayName(chat).toLowerCase();
      const lastMessage = chat.lastMessage?.content?.toLowerCase() ?? '';
      return displayName.includes(query) || lastMessage.includes(query);
    });
  }, [chats, searchQuery]);

  const handleChatSelect = (chatId: string) => {
    setSelectedChatId(chatId);
    setShowMobileMessages(true);
  };

  const handleCreateGroup = () => {
    setError('Group chat creation is not available yet.');
  };

  const handleSendMessage = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedChatId || !messageDraft.trim()) return;

    const content = messageDraft.trim();
    setMessageDraft('');
    try {
      const message = await sendMessageApi(selectedChatId, content);
      setMessages((prev) => [...prev, message]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to send message');
    }
  };

  const handleTyping = (value: string) => {
    setMessageDraft(value);
    if (!selectedChatId) return;

    sendTyping(true);

    if (typingTimeoutRef.current) {
      window.clearTimeout(typingTimeoutRef.current);
    }
    typingTimeoutRef.current = window.setTimeout(() => {
      sendTyping(false);
    }, 800);
  };

  return (
    <Container className="chat-app">
      <Shell
        mode="split"
        className={isBundle ? 'shell--with-navigation' : undefined}
        left={
          <ChatListPanel
            filteredChats={filteredChats}
            isLoading={isLoadingChats}
            searchQuery={searchQuery}
            onSearch={setSearchQuery}
            selectedChatId={selectedChatId}
            onSelectChat={handleChatSelect}
            onCreateGroup={handleCreateGroup}
            getChatDisplayName={getChatDisplayName}
            formatChatTimestamp={formatChatTimestamp}
            showMobileMessages={showMobileMessages}
            totalChats={chats.length}
          />
        }
        right={
          <ChatThreadPanel
            activeChat={activeChat}
            memberLabel={memberLabel}
            messages={messages}
            typingUsers={typingUsers}
            currentUserId={currentUserId}
            isLoadingMessages={isLoadingMessages}
            error={error}
            messageDraft={messageDraft}
            onMessageChange={handleTyping}
            onSendMessage={handleSendMessage}
            onBackToChats={() => setShowMobileMessages(false)}
            connectionStatus={connectionStatus}
            getChatDisplayName={getChatDisplayName}
            showMobileMessages={showMobileMessages}
          />
        }
        leftHidden={showMobileMessages}
        rightActive={showMobileMessages}
      />
    </Container>
  );
}
